import { AllocationPayload, VirtualWarehouseItem } from "@api/allocateManagementApi";
import throughShipmentApi, { RetailList, RetailVsCodeFilter } from "@api/throughShipment/throughShipmentApi";
import {
  AllocationChannelOption,
  AllocationDestinationType,
  TRYALL_STOCK_CHANNEL_ID,
  buildAllocationChannelOptions,
  buildRetailOptions,
} from "@constant/AllocationChannel";
import {
  clearDestinationData,
  fetchDestinationAllocatableQty,
  submitBatchAllocation,
} from "@redux/sharing/virtualWarehouseSlice";
import { useAppDispatch, useSelector } from "src/store";

import { ExclamationCircleOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Alert, Button, InputNumber, Modal, Select, Table, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

const renderExpiryBatch = (record: VirtualWarehouseItem) => {
  const { effectiveDate, batch } = record;
  if (!effectiveDate) return "-";
  return batch ? `${effectiveDate}_${batch}` : effectiveDate;
};

const ErrorText = styled(Typography.Text).attrs({ type: "danger" })`
  display: block;
  font-size: 12px;
`;

const RedAlert = styled(Alert)`
  .ant-alert-message {
    color: red !important;
    font-size: 18px;
    text-align: center;
  }
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 32px;
  row-gap: 8px;
  background: #e6f7ff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SummaryRow = styled.div`
  display: contents;
`;

const SummaryField = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: #8c8c8c;
`;

const SummaryValue = styled.span`
  font-size: 20px;
  font-weight: 500;
`;

interface AllocationTarget {
  targetId: number;
  locationType: AllocationDestinationType | undefined;
  retailerId: number | undefined;
  retailerName: string;
  qty: number | undefined;
  retailList: RetailList[];
}

interface Props {
  sourceItem: VirtualWarehouseItem;
  sourceDealerInput: AllocationChannelOption;
  onClose: () => void;
  onSuccess: () => void;
}

export default function SingleAllocationModal({ sourceItem, sourceDealerInput, onClose, onSuccess }: Props) {
  const dispatch = useAppDispatch();
  const { isFetchingSubmit, destinationAllocatableMap, isFetchingDestination } = useSelector(
    (state) => state.virtualWarehouseSlice,
  );

  const nextTargetIdRef = useRef(1);

  const [allocationTargets, setAllocationTargets] = useState<AllocationTarget[]>([
    { targetId: 0, locationType: undefined, retailerId: undefined, retailerName: "", qty: undefined, retailList: [] },
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearDestinationData());
    };
  }, [dispatch]);

  const locationTypeOptions = useMemo(
    () => buildAllocationChannelOptions(sourceDealerInput.kind),
    [sourceDealerInput.kind],
  );

  const handleAllocationChannelTypeChange = async (targetId: number, value: AllocationDestinationType) => {
    setAllocationTargets((prev) =>
      prev.map((target) =>
        target.targetId === targetId
          ? {
              ...target,
              locationType: value,
              retailerId: value === TRYALL_STOCK_CHANNEL_ID ? TRYALL_STOCK_CHANNEL_ID : undefined,
              retailerName: value === TRYALL_STOCK_CHANNEL_ID ? "總倉" : "",
              retailList: [],
            }
          : target,
      ),
    );

    if (value === TRYALL_STOCK_CHANNEL_ID) {
      dispatch(fetchDestinationAllocatableQty({ retailerId: TRYALL_STOCK_CHANNEL_ID, sku: [sourceItem.sku] }));
      return;
    }

    try {
      const res = await throughShipmentApi.fetchRetailList({
        regionTypes: value,
        isVs: RetailVsCodeFilter.HAS_VS_CODE,
      });
      setAllocationTargets((prevTargets) =>
        prevTargets.map((target) => (target.targetId === targetId ? { ...target, retailList: res.results } : target)),
      );
    } catch {
      // 保留原本吞掉錯誤的行為
    }
  };

  const handleDealerChange = (targetId: number, value: number, list: RetailList[]) => {
    const found = list.find((r) => r.id === value);
    setAllocationTargets((prev) =>
      prev.map((t) => (t.targetId === targetId ? { ...t, retailerId: value, retailerName: found?.name ?? "" } : t)),
    );
    dispatch(fetchDestinationAllocatableQty({ retailerId: value, sku: [sourceItem.sku] }));
  };

  const handleQtyChange = (targetId: number, value: number | undefined) => {
    setAllocationTargets((prev) => prev.map((t) => (t.targetId === targetId ? { ...t, qty: value } : t)));
  };

  const addAllocationTargets = () => {
    const newId = nextTargetIdRef.current;
    nextTargetIdRef.current += 1;
    setAllocationTargets((prev) => [
      ...prev,
      {
        targetId: newId,
        locationType: undefined,
        retailerId: undefined,
        retailerName: "",
        qty: undefined,
        retailList: [],
      },
    ]);
  };

  const removeTarget = (targetId: number) => {
    setAllocationTargets((prev) => prev.filter((r) => r.targetId !== targetId));
  };

  const validTotalQty = useMemo(
    () =>
      allocationTargets.filter((t) => t.retailerId !== undefined && t.qty).reduce((sum, t) => sum + (t.qty ?? 0), 0),
    [allocationTargets],
  );

  const remainingQty = sourceItem.canUseQty - validTotalQty;

  const hasConflict = useMemo(
    () => sourceItem.retailerName !== null && allocationTargets.some((t) => t.retailerName === sourceItem.retailerName),
    [allocationTargets, sourceItem.retailerName],
  );

  const getCumulativeQtyBefore = (index: number) =>
    allocationTargets
      .slice(0, index)
      .filter((t) => t.retailerId !== undefined && t.qty)
      .reduce((sum, t) => sum + (t.qty ?? 0), 0);

  const isConfirmDisabled =
    allocationTargets.every((t) => !t.qty) ||
    allocationTargets.some((t) => t.qty && t.retailerId === undefined) ||
    allocationTargets.some((t) => t.retailerId !== undefined && !t.qty) ||
    remainingQty < 0 ||
    hasConflict;

  const handleConfirm = () => {
    const payloads = allocationTargets
      .map((r): AllocationPayload | null => {
        if (!r.qty || r.retailerId === undefined) return null;
        const common = { destRetailerId: r.retailerId, qty: r.qty };
        if (sourceItem.alloId !== undefined) return { alloId: sourceItem.alloId, ...common };
        if (sourceItem.stockId != null) return { stockId: sourceItem.stockId, ...common };
        return null;
      })
      .filter((p): p is AllocationPayload => p !== null);
    if (payloads.length === 0) return;
    dispatch(submitBatchAllocation(payloads))
      .unwrap()
      .then(() => onSuccess())
      .catch(() => {});
  };

  const columns: ColumnsType<AllocationTarget> = [
    {
      key: "location",
      title: "配貨位置",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Select
            placeholder="請選擇"
            style={{ width: 160 }}
            options={locationTypeOptions}
            value={record.locationType}
            onChange={(v) => handleAllocationChannelTypeChange(record.targetId, v)}
          />
          {record.locationType !== undefined && record.locationType !== TRYALL_STOCK_CHANNEL_ID && (
            <Select
              showSearch
              allowClear
              placeholder="請選擇通路"
              style={{ width: 160 }}
              options={buildRetailOptions(record.retailList)}
              value={record.retailerId}
              filterOption={(input, option) =>
                typeof option?.label === "string" && option.label.toLowerCase().includes(input.toLowerCase())
              }
              onChange={(v) => handleDealerChange(record.targetId, v, record.retailList)}
            />
          )}
          {record.qty && record.retailerId === undefined && <ErrorText>請選擇配貨位置</ErrorText>}
        </div>
      ),
    },
    {
      key: "qty",
      title: "配貨數量",
      render: (_, record, index) => {
        const cumulativeBefore = getCumulativeQtyBefore(index);
        const isExceeded =
          record.retailerId !== undefined &&
          record.qty !== undefined &&
          cumulativeBefore + record.qty > sourceItem.canUseQty;
        const isMissingQty = record.retailerId !== undefined && !record.qty;
        let errorMsg: string | null = null;
        if (isExceeded) errorMsg = "配貨數量超過當前可分配數";
        else if (isMissingQty) errorMsg = "請填寫配貨數量";
        return (
          <div>
            <InputNumber
              min={1}
              value={record.qty}
              onChange={(v) => handleQtyChange(record.targetId, v ?? undefined)}
            />
            {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
          </div>
        );
      },
    },
    {
      key: "beforeQty",
      title: (
        <span>
          配貨前可分配數&nbsp;
          <Tooltip title="此商品在配貨位置，經配貨前的可分配數">
            <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </span>
      ),
      render: (_, record) => {
        if (record.retailerId === undefined) return 0;
        if (isFetchingDestination) return "...";
        return destinationAllocatableMap[`${record.retailerId}:${sourceItem.sku}`] ?? 0;
      },
    },
    {
      key: "afterQty",
      title: (
        <span>
          配貨後可分配數&nbsp;
          <Tooltip title="此商品在配貨位置，經配貨後的可分配數">
            <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
          </Tooltip>
        </span>
      ),
      render: (_, record) => {
        if (record.retailerId === undefined) return 0;
        const before = destinationAllocatableMap[`${record.retailerId}:${sourceItem.sku}`] ?? 0;
        return before + (record.qty ?? 0);
      },
    },
    {
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Button icon={<PlusOutlined />} size="small" onClick={addAllocationTargets} />
          {allocationTargets.length > 1 && (
            <Button icon={<MinusOutlined />} size="small" danger onClick={() => removeTarget(record.targetId)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="配貨"
      open
      onCancel={onClose}
      width={1100}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={isFetchingSubmit} disabled={isConfirmDisabled} onClick={handleConfirm}>
            確認
          </Button>
        </>
      }
    >
      <SummaryCard>
        <SummaryRow>
          <SummaryField>
            <SummaryLabel>SKU</SummaryLabel>
            <SummaryValue>{sourceItem.sku}</SummaryValue>
          </SummaryField>
          <SummaryField>
            <SummaryLabel>商品名稱</SummaryLabel>
            <SummaryValue>{sourceItem.productName}</SummaryValue>
          </SummaryField>
          <SummaryField>
            <SummaryLabel>效期_批號</SummaryLabel>
            <SummaryValue>{renderExpiryBatch(sourceItem)}</SummaryValue>
          </SummaryField>
        </SummaryRow>
        <SummaryRow>
          <SummaryField>
            <SummaryLabel>來源通路</SummaryLabel>
            <SummaryValue>{sourceItem.retailerName ?? "總倉"}</SummaryValue>
          </SummaryField>
          <SummaryField>
            <SummaryLabel>
              當前可分配數{" "}
              <Tooltip title="此商品在所有配貨位置的總可分配數">
                <ExclamationCircleOutlined style={{ color: "#1890ff" }} />
              </Tooltip>
            </SummaryLabel>
            <SummaryValue style={{ color: remainingQty < 0 ? "#ff4d4f" : undefined }}>{remainingQty}</SummaryValue>
          </SummaryField>
        </SummaryRow>
      </SummaryCard>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>當前選擇資料</div>
      {hasConflict && (
        <RedAlert message="通路不可與配貨位置相同" type="error" showIcon={false} style={{ marginBottom: 12 }} />
      )}
      {sourceItem.canUseQty <= 0 ? (
        <ErrorText style={{ display: "block", marginBottom: 16, fontSize: 20 }}>此商品當前可分配數不足</ErrorText>
      ) : (
        <Table
          columns={columns}
          dataSource={allocationTargets}
          rowKey="targetId"
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
      )}
    </Modal>
  );
}
